import { IkeToken, IkeUserRole, isCollectionVariant } from '@ikegps/core-api';
import { CollectionModelFieldTrueSize } from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { TopologicalEditorController } from '.';
import { DrawButton } from '../../components/button/draw.button';
import { Carousel } from '../../components/carousel';
import { CenteredMessage } from '../../components/centeredMessage';
import { CollapsedRail, PanelCollapseState, panelVisibilityValue, panelWidthValue } from '../../components/collapsible';
import { MaterialIcon } from '../../components/icon/icons';
import { Sidebar, SidebarCSS } from '../../components/sidebar';
import { ResizeHandle } from '../../components/resizable';
import { addStyles, DefaultTheme } from '../../components/theme';
import { T } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { CapturePreview, CapturePreviewType } from '../../modules/capture.preview/capture.preview';
import { CollectionEdit } from '../../modules/collection.view/collection.view';
import { CSS as DrawToolbarCSS } from '../../modules/draw.toolbar/draw.toolbar.style';
import { MapModeLegend } from '../../modules/map.mode.legend';
import { CSS as MapModeLegendCSS } from '../../modules/map.mode.legend/map.mode.legend.style';
import { DistanceMeasurePopup } from '../../modules/distance.measure.popup';
import { LineDrawPopup } from '../../modules/line.draw.popup';
import { MeasurementPopup } from '../../modules/measurement.popup';
import { TopologicalEditorCollectionList } from '../../modules/topological.editor.collection.list';
import { MapPrintController, MapPrintFrame } from '../../modules/topological.editor.print';
import { CSS as TopologicalEditorCollectionListCSS } from '../../modules/topological.editor.collection.list/topological.editor.collection.list.style';
import { TopologicalEditorToolbar } from '../../modules/topological.editor.toolbar';
import type { ITopologicalEditorToolbarOptions } from '../../modules/topological.editor.toolbar/topological.editor.toolbar';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { scrollSelectedIntoListView } from '../../util/scroll-into-list-view';
import { IkePage, type IkePageConfig, IkePageName } from '../page';
import { MapModeInputEvents } from './mm.events';

export const CSS = {
	container: 'c-TopologicalEditor',
	content: 'c-TopologicalEditor__Content',
	contentFraming: 'c-TopologicalEditor__Content--framing',
	divider: 'c-TopologicalEditor__Divider',
	smallContainer: 'c-TopologicalEditor__SmallContainer',
	streetviewContainer: 'c-TopologicalEditor__StreetviewContainer',
	overlaySave: 'c-TopologicalEditor__OverlaySave',
	framingScrim: 'c-TopologicalEditor__FramingScrim',
	toggleableElement: 'c-ToggleableElement',
};

const LIST_PANEL_VAR = '--ike-panel-mapmode-list';
const FORM_PANEL_VAR = '--ike-panel-mapmode-form';
const LEGEND_PANEL_VAR = '--ike-panel-mapmode-legend';
const STREETVIEW_PANEL_VAR = '--ike-panel-mapmode-streetview';

/** Shared with the resize handles below so the clamp and the CSS floors can't drift apart. */
const LIST_MIN_WIDTH = 350;
const FORM_MIN_WIDTH = 360;
const MAP_MIN_WIDTH = 400;
const LIST_MIN_HEIGHT = 120;
const LEGEND_MIN_HEIGHT = 80;
const FORM_MIN_HEIGHT = 120;
const STREETVIEW_MIN_HEIGHT = 120;

const Style = {
	[CSS.container]: {
		height: '100%',
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		// Pinned here rather than in the shared Sidebar component: the properties and floors belong to this page.
		[`& > .${SidebarCSS.leftSidebar}`]: {
			width: panelWidthValue(LIST_PANEL_VAR, `var(${LIST_PANEL_VAR}, 20%)`),
			'min-width': panelWidthValue(LIST_PANEL_VAR, `${LIST_MIN_WIDTH}px`),
			visibility: panelVisibilityValue(LIST_PANEL_VAR),
		},
		[`& > .${SidebarCSS.rightSidebar}`]: {
			width: `var(${FORM_PANEL_VAR}, 20%)`,
			'min-width': `${FORM_MIN_WIDTH}px`,
		},
		[`& .${TopologicalEditorCollectionListCSS.container}`]: {
			flex: '1 1 0',
			'min-height': `${LIST_MIN_HEIGHT}px`,
		},
		[`& .${MapModeLegendCSS.container}`]: {
			flex: `0 1 var(${LEGEND_PANEL_VAR}, 30%)`,
			'min-height': `${LEGEND_MIN_HEIGHT}px`,
			// The legend module caps itself at 30%, which would silently swallow any drag past that.
			'max-height': 'none',
			// The divider writes a border-box size into the basis; without this the padding is added on top.
			'box-sizing': 'border-box',
		},
	},

	// `flex` rather than a `width`: the map has to be the row's only flexible item, or it competes for shrink
	// and the sidebars collapse back toward min-width instead of holding the width they were dragged to.
	[CSS.content]: {
		flex: '1 1 0',
		'min-width': `${MAP_MIN_WIDTH}px`,
		borderLeft: `1px solid ${DefaultTheme.font.color.divider}`,
		position: 'relative',
	},

	[CSS.divider]: {
		width: '100%',
		height: '1px',
		margin: '6px 0',
		background: DefaultTheme.font.color.divider,
	},

	[CSS.smallContainer]: {
		flex: '1 1 0',
		'min-height': `${FORM_MIN_HEIGHT}px`,
		overflow: 'hidden',
	},

	[CSS.streetviewContainer]: {
		flex: `0 1 var(${STREETVIEW_PANEL_VAR}, 50%)`,
		'min-height': `${STREETVIEW_MIN_HEIGHT}px`,
		'box-sizing': 'border-box',
		overflow: 'hidden',
	},

	[CSS.overlaySave]: {
		position: 'fixed',
		display: 'block',
		width: '100%',
		height: '100%',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		'background-color': 'rgba(0,0,0,0.5)',
		'z-index': 200,
		cursor: 'pointer',
	},
	[CSS.toggleableElement]: {
		minHeight: '0',
		flexGrow: '1',
	},

	// While framing, a full-viewport scrim blocks the side panels (toolbar, collection list, edit form,
	// photomeasure). The map content is raised above it so panning and Show/Hide labels still work.
	[CSS.framingScrim]: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		'background-color': 'rgba(0,0,0,0.35)',
		'z-index': 140,
	},
	[CSS.contentFraming]: {
		'z-index': 145,
	},
};

addStyles(Style);

export const TopologicalEditorPageConfig: IkePageConfig = {
	type: 'internal',
	name: IkePageName.topologicalEditor,
	path: ['/mapmode/:jobId', '/mapmode/:jobId/:collectionUUID'],
	auth: {
		required: true,

		tokens: [IkeToken.FEATURE_MAP_MODE],
		userLevelBlackList: [IkeUserRole.VIEWER, IkeUserRole.DOWNLOADER],
	},
	layout: { nav: false },
};

export class TopologicalEditorPage extends IkePage {
	controller = new TopologicalEditorController();

	streeviewSidebar = new Sidebar();
	collectionSidebar = new Sidebar();
	collectionEdit: CollectionEdit | undefined;

	// Only the map remeasures: the right sidebar's width is a share of the page, which collapsing the left
	// panel doesn't change, so the streetview pane it carries keeps its size.
	listCollapse = new PanelCollapseState({
		cssVar: LIST_PANEL_VAR,
		onToggle: () => Services.Map.notifyResize(),
	});

	listHandle = new ResizeHandle({
		axis: 'x',
		cssVar: LIST_PANEL_VAR,
		target: 'previous',
		min: LIST_MIN_WIDTH,
		minAdjacent: MAP_MIN_WIDTH,
		storageKey: 'panel.size/mapmode/list',
		onResizeEnd: () => Services.Map.notifyResize(),
	});

	formHandle = new ResizeHandle({
		axis: 'x',
		cssVar: FORM_PANEL_VAR,
		target: 'next',
		min: FORM_MIN_WIDTH,
		minAdjacent: MAP_MIN_WIDTH,
		storageKey: 'panel.size/mapmode/form',
		onResizeEnd: () => {
			Services.Map.notifyResize();
			Services.Streetview.notifyResize();
		},
	});

	legendHandle = new ResizeHandle({
		axis: 'y',
		cssVar: LEGEND_PANEL_VAR,
		target: 'next',
		min: LEGEND_MIN_HEIGHT,
		minAdjacent: LIST_MIN_HEIGHT,
		storageKey: 'panel.size/mapmode/legend',
	});

	streetviewHandle = new ResizeHandle({
		axis: 'y',
		cssVar: STREETVIEW_PANEL_VAR,
		target: 'next',
		min: STREETVIEW_MIN_HEIGHT,
		minAdjacent: FORM_MIN_HEIGHT,
		storageKey: 'panel.size/mapmode/streetview',
		onResizeEnd: () => Services.Streetview.notifyResize(),
	});

	events: MapModeInputEvents;

	mapPrint = new MapPrintController({
		getDepartmentName: () => Services.Department.current?.displayName ?? '',
		getJobName: () => this.jobName,
		prepareCapture: (showCollectionLabels, showVectorLabels, showVectors, showLines) => {
			const overlay = this.controller.map.overlay;
			if (!overlay) {
				return () => {};
			}
			// Collection/vector labels, vectors, and lines are set independently from the modal checkboxes
			// at capture time, regardless of the on-map toggle, then restored. Zoom gating is bypassed so
			// the checkboxes stay authoritative at any zoom level.
			const previousCollectionLabels = overlay.viewCollectionLabels;
			const previousVectorLabels = overlay.viewVectorLabels;
			const previousVectors = overlay.viewVectors;
			const previousLines = overlay.viewLines;
			overlay.viewCollectionLabels = showCollectionLabels;
			overlay.viewVectorLabels = showVectorLabels;
			overlay.viewVectors = showVectors;
			overlay.viewLines = showLines;
			overlay.bypassLabelZoomGating = true;
			// Suppress the hover/selection focus dim so nothing prints faded, even if a feature is
			// selected or gets hovered during the async capture.
			overlay.suppressFocusDim = true;
			return () => {
				overlay.viewCollectionLabels = previousCollectionLabels;
				overlay.viewVectorLabels = previousVectorLabels;
				overlay.viewVectors = previousVectors;
				overlay.viewLines = previousLines;
				overlay.bypassLabelZoomGating = false;
				overlay.suppressFocusDim = false;
			};
		},
		// Lock map tools and pole/vector selection while the print frame is being positioned.
		onFramingChange: (active) => this.controller.setPrintFraming(active),
		getLineLegendItems: () => this.controller.visibleLineLegendItems,
		getAssetTypeLegendItems: () => this.controller.visibleAssetTypeLegendItems,
	});

	@observable.ref private _carousel: Carousel | null = null;
	@observable private _carouselCollectionRevision: string | null = null;

	constructor() {
		super(TopologicalEditorPageConfig);
		makeObservable(this);

		Services.Window.bindNavigateAway(T.DISCARD_CHANGES_CONFIRM, this.checkExit);

		// Safe this early: the router only resolves a route once tokens have loaded (route.service firstLoad).
		this.listCollapse.ensureRestored();
		// A department switch keeps this page instance, so the token can drop without init running again.
		this.reaction(
			() => this.listCollapse.isEnabled,
			() => this.listCollapse.syncToToken()
		);

		this.events = new MapModeInputEvents(this);
		this.events.init();

		this.reaction(
			() => this.controller.selectedCollectionRevision,
			(revision) => {
				if (revision != null) {
					scrollSelectedIntoListView(
						TopologicalEditorCollectionListCSS.container,
						TopologicalEditorCollectionListCSS.selected
					);
				}
			}
		);

		// Track the selected capture, not just the collection: clicking a MidSpan dot on the pole that is
		// ALREADY selected changes only the capture, and the carousel getter short-circuits on the cached
		// instance, so nothing would move it. Guarded on a real change so `goTo` -> `onSlideChange` ->
		// `setSelectedCaptureId` cannot loop.
		this.reaction(
			() => this.selectedCaptureIndex,
			(index) => {
				const carousel = this._carousel;
				if (carousel != null && index >= 0 && carousel.currentIndex !== index) {
					carousel.goTo(index);
				}
			}
		);
	}

	@action.bound private checkExit(): boolean {
		// An in-progress (unconfirmed) line blocks navigation like a dirty pole, even though
		// it commits nothing yet — so the user can't silently lose it by navigating away.
		return this.controller.hasChanges || this.controller.lineDrawState.isActive;
	}

	override onRemove() {
		this.controller.destroy();
		Services.Window.unbindNavigateAway(this.checkExit);
		Services.Map.map.getStreetView().setVisible(false);

		this.mapPrint.cancel();

		this.events.destroy();
	}

	get content() {
		return m(
			'div',
			{
				className: `${CSS.content} ${this.controller.contentClassNames} ${
					this.mapPrint.isFraming ? CSS.contentFraming : ''
				}`,
			},
			[
				this.controller.map.view(),
				m('div', { className: DrawToolbarCSS.container }, [
					m('div', { className: DrawToolbarCSS.column }, [m('div', { className: DrawToolbarCSS.group }, this.tools)]),
				]),
				this.controller.measurementState.isActive
					? MeasurementPopup.view({
							onClose: () => this.toggleMeasurement(),
							onPauseChange: () => this.updateMeasurementCursor(),
							measurementState: this.controller.measurementState,
						})
					: null,
				this.controller.distanceMeasureState.isActive
					? DistanceMeasurePopup.view({
							onClose: () => this.toggleDistanceMeasure(),
							distanceMeasureState: this.controller.distanceMeasureState,
						})
					: null,
				this.controller.lineDrawState.isActive
					? LineDrawPopup.view({
							onComplete: () => this.controller.completeLineDraw(),
							onCancel: () => this.controller.cancelLineDraw(),
							lineDrawState: this.controller.lineDrawState,
						})
					: null,
				this.mapPrint.isFraming
					? MapPrintFrame.view({
							region: this.mapPrint.frameRegion,
							pageCount: this.mapPrint.pageCount,
							busy: this.mapPrint.isCapturing,
							onAddPage: () => this.mapPrint.addPage(),
							onExport: () => this.mapPrint.exportFraming(),
							onCancel: () => this.mapPrint.cancelFraming(),
						})
					: null,
			]
		);
	}

	get carousel(): Carousel | null {
		const currentRevision = this.controller.selectedCollectionRevision;

		// Return cached carousel if it's for the same collection
		if (this._carousel && this._carouselCollectionRevision === currentRevision) {
			return this._carousel;
		}

		// Update cache using action for the new collection
		this.updateCarousel(currentRevision);

		return this._carousel;
	}

	@action private updateCarousel(currentRevision: string | null): void {
		// Clear cache if collection changed
		this._carousel = null;
		this._carouselCollectionRevision = currentRevision;

		const selectedCollection = this.controller.selectedCollection;
		if (selectedCollection) {
			let captures: MithrilVNode[] = [];

			selectedCollection.forEachField((field) => {
				if (CollectionModelFieldTrueSize.isMeasurableIkePhotoField(field)) {
					const captureElements = field.getCaptures().map((capture) =>
						CapturePreview.view(selectedCollection, capture, {
							type: CapturePreviewType.MEDIUM,
							truesize: false,
							icon: true,
							crosshair: false,
						})
					);

					captures = captures.concat(captureElements);
				}
			});

			if (captures.length) {
				this._carousel = new Carousel({
					slides: captures,
					// Open on the selected capture when there is one — clicking a MidSpan dot selects that photo,
					// and rebuilding at index 0 would drop the user back on the pole's first shot.
					index: Math.max(0, this.selectedCaptureIndex),
					onSlideChange: (index) => this.onSlideChange(index),
				});
			}
		}
	}

	get tools() {
		const isMeasurementActive = this.controller.measurementState.isActive && !this.controller.measurementState.isPaused;
		const isDistanceMeasureActive = this.controller.distanceMeasureState.isActive;
		const isRelocatePoleActive = this.controller.isRelocatePoleActive;
		const isAnyMeasureActive = isMeasurementActive || isDistanceMeasureActive;
		// Drawing a line or span/collection linking locks every other map tool, since each owns
		// map interaction until it's completed or cancelled.
		const locked =
			this.mapPrint.isFraming || this.controller.lineDrawState.isActive || CurrentState.CollectionLink.isLinking;

		// The relocate ("adjust pole location") tool edits a pole's location, so it is not offered when the
		// selected pole is a read-only variant.
		const selectedCollection = this.controller.selectedCollection;
		const canRelocatePole = selectedCollection != null && !isCollectionVariant(selectedCollection);

		return [
			this.controller.getFormHasLocationField()
				? DrawButton.view(MaterialIcon.ADD_LOCATION, {
						flip: false,
						title: T.TOPO_TOOL_CREATE_POLE,
						disabled: isAnyMeasureActive || locked,
						selected: this.controller.isCreatePoleMode,
						solidBackground: true,
						onclick: () => this.controller.toggleCreatePoleMode(),
					})
				: null,
			canRelocatePole
				? DrawButton.view(MaterialIcon.EDIT_LOCATION, {
						flip: false,
						title: T.TOPO_TOOL_RELOCATE_POLE,
						disabled: isAnyMeasureActive || locked,
						selected: isRelocatePoleActive,
						solidBackground: true,
						onclick: () => this.controller.useTool('relocatePole'),
					})
				: null,
			DrawButton.view(MaterialIcon.VISIBILITY, {
				flip: false,
				title: T.TOPO_SHOW_HIDE_LABELS,
				disabled: false,
				selected: this.controller.map.overlay?.viewCollectionLabels,
				solidBackground: true,
				onclick: () => this.controller.map.overlay?.toggleViewTitles(),
			}),
			DrawButton.view(MaterialIcon.MAGIC_WAND, {
				title: T.TOPO_TOOL_SNAP,
				disabled: isAnyMeasureActive || isRelocatePoleActive || locked,
				selected: false,
				solidBackground: true,
				onclick: () => this.controller.bulkSnap(),
			}),
			DrawButton.view(MaterialIcon.LINEAR_SCALE, {
				title: T.TOPO_TOOL_MEASURE,
				disabled: isRelocatePoleActive || isDistanceMeasureActive || locked,
				selected: this.controller.measurementState.isActive,
				solidBackground: true,
				onclick: () => this.toggleMeasurement(),
			}),
			DrawButton.view(MaterialIcon.HEIGHT, {
				flip: true,
				title: T.TOPO_TOOL_DISTANCE_MEASURE,
				disabled: isRelocatePoleActive || isMeasurementActive || locked,
				selected: isDistanceMeasureActive,
				solidBackground: true,
				onclick: () => this.toggleDistanceMeasure(),
			}),
		];
	}

	@action.bound private toggleMeasurement() {
		const isActive = this.controller.measurementState.isActive;
		this.controller.measurementState.setActive(!isActive);

		if (!isActive) {
			// Deactivate distance measure if active
			this.controller.distanceMeasureState.reset();
			this.controller.lineDrawState.reset();
			// Exit any active tool (e.g., relocate pole) when entering measurement mode
			this.controller.exitTool();
			this.updateMeasurementCursor();
		} else {
			this.controller.getMapModeMap()?.setOptions({ draggableCursor: '' });
			// When closing measurement mode, clear the points
			this.controller.measurementState.clearPoints();
		}
	}

	@action.bound private toggleDistanceMeasure() {
		const isActive = this.controller.distanceMeasureState.isActive;
		this.controller.distanceMeasureState.setActive(!isActive);

		if (!isActive) {
			// Deactivate ruling span if active
			this.controller.measurementState.reset();
			this.controller.lineDrawState.reset();
			// Exit any active tool (e.g., relocate pole)
			this.controller.exitTool();
			this.controller.getMapModeMap()?.setOptions({ draggableCursor: 'crosshair' });
		} else {
			this.controller.getMapModeMap()?.setOptions({ draggableCursor: '' });
		}
	}

	private updateMeasurementCursor() {
		const isPaused = this.controller.measurementState.isPaused;
		this.controller.getMapModeMap()?.setOptions({ draggableCursor: isPaused ? '' : 'crosshair' });
	}

	get editCollectionForm() {
		if (this.controller.selectedCollectionEdit) {
			let carouselOrEmpty = this.carousel;
			let carouselContainer = m(
				'div',
				{
					className: CSS.toggleableElement,
					style: { display: this.controller.selectedCollectionEdit.isCarouselShown ? 'block' : 'none' },
				},
				carouselOrEmpty !== null ? carouselOrEmpty.view() : CenteredMessage.view(T.NO_CAPTURES)
			);
			return m(
				'div',
				{ className: CSS.smallContainer },
				new CollectionEdit(this.controller.selectedCollectionEdit).view(carouselContainer)
			);
		}
		return null;
	}

	@computed get jobName() {
		return this.controller.tag ? this.controller.tag.name : '';
	}

	@action.bound private startMapPrint(): void {
		// Stand down any active measurement / drawing tool so it can't conflict with the capture.
		this.controller.measurementState.reset();
		this.controller.distanceMeasureState.reset();
		// cancelLineDraw (not reset) so the crosshair cursor is cleared too.
		this.controller.cancelLineDraw();
		this.controller.exitTool();
		this.mapPrint.open();
	}

	get titleBar(): ITopologicalEditorToolbarOptions {
		const titleBarParams: ITopologicalEditorToolbarOptions = {
			title: this.jobName,
			collapseToggle: this.listCollapse.viewToggle(),
			printButton: { onClick: () => this.startMapPrint() },
			selectedCollectionData: {
				onClick: (e) => {
					const department = Services.Department.current;
					if (e == null || department == null) {
						return;
					}
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					Services.Window.confirmPageChange(
						e,
						Services.Route.getRouteURL(IkePageName.collectionMeasure, {
							d: department.objectId,
							t: this.controller.tag.objectId,
							...(this.controller.selectedCollection && {
								collection: this.controller.selectedCollection.uuid,
							}),
						})
					);
				},
			},
			saveButton: {
				// Block saving while a line is mid-draw: it isn't a complete edit yet.
				disabled: !(this.controller.hasChanges || this.controller.isSaving) || this.controller.lineDrawState.isActive,
				onClick: () => {
					this.controller.batchSave();
				},
				isSaving: this.controller.isSaving,
			},
		};

		return titleBarParams;
	}

	get nodes() {
		if (Services.Department.current == null) {
			return null;
		}

		if (!Services.Streetview.isReady) {
			return null;
		}

		if (this.controller.routeCollectionUUID) {
			const collection = this.controller.collectionModels.find((c) => c.uuid === this.controller.routeCollectionUUID);
			if (collection && collection.revision !== this.controller.selectedCollectionRevision) {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				Services.Map.isReadyPromise.then(() => {
					this.controller.setSelectedCollectionRevision(collection.revision);
				});
			}
		}

		return [
			m(
				'div',
				{
					className: CSS.container,
				},
				[
					// The legend rides along inside the sidebar, as the mockups show.
					CollapsedRail.view(this.listCollapse, this.jobName),
					this.collectionSidebar.view(true, [
						TopologicalEditorToolbar.view(this.titleBar),
						TopologicalEditorCollectionList.view({
							onItemClick: (revision: string) => {
								this.controller.setSelectedCollectionRevision(revision);
								this.controller.zoomToSelectedCollection();
							},
							collections: this.controller.collectionModels,
							selectedRevision: this.controller.selectedCollectionRevision,
							collectionsEditController: this.controller,
						}),
						// Falls back to the hairline because the handle renders nothing without the token.
						this.legendHandle.view() ?? m('div', { className: CSS.divider }),
						MapModeLegend.view(this.controller.map.overlay, [
							...this.controller.lineLegendItems,
							...this.controller.assetTypeLegendItems,
						]),
					]),
					this.listCollapse.isCollapsed ? null : this.listHandle.view(),
					this.content,
					// Tracks the sidebar, or it would dangle against the window edge with nothing to resize.
					this.controller.selectedCollectionRevision ? this.formHandle.view() : null,
					this.controller.selectedCollectionRevision &&
						this.streeviewSidebar.view(false, [
							this.editCollectionForm,
							this.streetviewHandle.view() ?? m('div', { className: CSS.divider }),
							m('div', { className: CSS.streetviewContainer }, this.controller.streetview.view()),
						]),
				]
			),
			this.mapPrint.isFraming ? m('div', { className: CSS.framingScrim }) : null,
			this.controller.isSaving ? m('div', { className: CSS.overlaySave }) : null,
		];
	}

	override onBeforeUnload() {
		this.controller.collectionEditControllers.forEach((controller) => {
			controller.discardAndDestroyEdit();
		});
	}

	@action.bound private onSlideChange(index: number): void {
		const captureIds = this.controller.getCaptureIds();
		const captureId = captureIds[index];
		if (captureId) {
			this.controller.setSelectedCaptureId(captureId);
		}
	}

	/**
	 * Where the selected capture sits in the carousel, or -1. `getCaptureIds()` walks the IKE photo fields in
	 * the same order `updateCarousel` builds slides, so the index is shared between them.
	 */
	@computed private get selectedCaptureIndex(): number {
		const captureId = this.controller.selectedCaptureId;
		if (captureId == null) {
			return -1;
		}
		return this.controller.getCaptureIds().indexOf(captureId);
	}
}
